import type { NormalizedCacheObject } from "@apollo/client/core";
import { ApolloClient, ApolloLink, createHttpLink, defaultDataIdFromObject, InMemoryCache } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { getDataFromTree } from "@apollo/client/react/ssr";
import * as Sentry from "@sentry/nextjs";
import isBrowser from "lib/utils/isBrowser";
import withApollo from "next-with-apollo";
import type { TypedTypePolicies } from "types/typePolicies";
import { setAuthorization } from "../components/auth/Auth";
import possibleTypes from "../types/possibleTypes";

const typePolicies: TypedTypePolicies = {};

export default withApollo(
  ({ ctx, initialState }) => {
    return fromInitialState(initialState, [
      new RetryLink({
        delay: {
          initial: 300,
          max: Infinity,
          jitter: true,
        },
        attempts: {
          max: 7,
          retryIf: (error) => {
            // Disable retries for "Bad Request" responses
            if (error.name === "ServerError" && typeof error.statusCode === "number" && error.statusCode === 400) {
              return false;
            }

            return !!error;
          },
        },
      }),
      setAuthorization(ctx),
      sentryErrorHandlerLink,
    ]);
  },
  {
    getDataFromTree,
  },
);

// eslint-disable-next-line default-param-last
function fromInitialState(initialState = {}, links: ApolloLink[]): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    connectToDevTools: isBrowser(),
    ssrMode: true,
    link: ApolloLink.from([
      ...links,
      createHttpLink({
        uri: process.env.API_URL,
        useGETForQueries: false,
      }),
    ]),
    cache: new InMemoryCache({
      addTypename: true,
      typePolicies,
      possibleTypes: possibleTypes.possibleTypes,
      dataIdFromObject: (responseObject) => {
        if (responseObject.__typename === "IssuerLogo" && responseObject.url) {
          return `${responseObject.__typename}:${responseObject.url}`;
        }
        return defaultDataIdFromObject(responseObject);
      },
    }).restore(initialState),
  });
}

const IGNORE_ERRORS: RegExp[] = [
  /User not found/i,
  // Authentication issues
  /Invalid credentials/i,
  /The email is already verified/i,
  /The provided password doesn't match/i,
  /Looks like a disposable email domain\/provider was used/i,
  /You're connected with Google without a password set/i,
  // Stripe related errors
  /Your card does not support this type of purchase./i,
  /Your card was declined./i,
  /Your card number is incorrect./i,
  /You're not allowed to perform this operation/i,
  /You're not allowed to perform this action/i,
  /This asset is already in your portfolio/i,
  /You're not authorized/i,
];

const sentryErrorHandlerLink = onError(({ graphQLErrors, networkError, operation }) => {
  Sentry.withScope((scope) => {
    scope.setTags({
      operationName: operation.operationName,
    });

    scope.setExtras({
      operationName: operation.operationName,
      variables: operation.variables,
    });

    if (operation.query.loc && operation.query.loc.source && operation.query.loc.source.body) {
      scope.setExtras({
        query: operation.query.loc.source.body,
      });
    }

    (graphQLErrors || []).forEach((graphQLError) => {
      const shouldIgnore = IGNORE_ERRORS.some((ignoreErrorRegex) => {
        return ignoreErrorRegex.test(graphQLError.message);
      });

      if (shouldIgnore) {
        return;
      }

      Sentry.captureMessage(graphQLError.message, "error");
    });

    if (networkError) {
      Sentry.captureException(networkError);
    }
  });
});
